import Box from '@mui/material/Box';
import humanizeDuration from 'humanize-duration';
import { DateTime } from 'luxon';
import React, { useEffect, useState } from 'react';
import { useInterval } from 'usehooks-ts';

const shortEnglishHumanizer = humanizeDuration.humanizer({
  language: 'shortEn',
  languages: {
    shortEn: {
      y: () => 'years',
      mo: () => 'months',
      w: () => 'weeks',
      d: () => 'days',
      h: () => 'hours',
      m: () => 'mins',
      s: () => 'secs',
      ms: () => 'msecs',
    },
  },
});

function TimeDuration({
  jsDate,
  invertSign = false,
  titleDisplay = 'remainingTime',
}: {
  jsDate: Date;
  invertSign?: boolean;
  titleDisplay?: 'remainingTime' | 'absoluteTime';
}) {
  const computeStrings = () => {
    const diffMs =
      DateTime.now().diff(DateTime.fromJSDate(jsDate)).milliseconds *
      (invertSign ? -1 : 1);
    const short = shortEnglishHumanizer(diffMs, { largest: 1, round: true });
    const full =
      titleDisplay === 'remainingTime'
        ? humanizeDuration(diffMs, { round: true })
        : DateTime.fromJSDate(jsDate).toLocaleString(DateTime.DATETIME_FULL);
    return { short, full };
  };

  const initial = computeStrings();
  const [shortString, setShortString] = useState(initial.short);
  const [fullString, setFullString] = useState(initial.full);

  const updateStrings = () => {
    const { short, full } = computeStrings();
    setShortString(short);
    setFullString(full);
  };

  // Adaptive update interval: every second when < 5 min, every 5s when < 1 hr, else 30s
  const adaptiveInterval = () => {
    const diffMs = Math.abs(DateTime.now().diff(DateTime.fromJSDate(jsDate)).milliseconds);
    if (diffMs < 5 * 60 * 1000) return 1000;
    if (diffMs < 60 * 60 * 1000) return 5000;
    return 30000;
  };

  useEffect(updateStrings, [invertSign, jsDate, titleDisplay]); // eslint-disable-line react-hooks/exhaustive-deps
  useInterval(updateStrings, adaptiveInterval());

  return (
    <Box display={'inline'} title={fullString}>
      {shortString}
    </Box>
  );
}

export default TimeDuration;
